/**
 * English: Bidirectional WebSocket hook for multi-turn chat — primary realtime path vs HTTP + polling alone.
 * Thai: Hook WebSocket แบบ 2-way สำหรับแชทหลายรอบ — ช่องทาง realtime หลักเมื่อไม่ได้ใช้แค่ HTTP + polling
 *
 * One persistent WS per component mount. After each task completes the connection
 * stays open (status → 'idle') ready for the next message.
 * WS เปิดค้าง 1 connection ต่อ mount; หลัง task เสร็จ status กลับเป็น 'idle' พร้อมรับข้อความใหม่
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { getWsChatUrl } from '../config/api';
import { ChatMessage, FullTaskState } from '../types';
import {
  getStoredDeviceCommandPassword,
  getStoredLoginOrgName,
  getStoredLoginUsername,
} from '../utils/deviceCommandPassword';

export type WsStatus =
  | 'idle'
  | 'connecting'
  | 'streaming'
  | 'completed'
  | 'stopped'
  | 'timeout'
  | 'error';

/** Wire mode for WS `send` — legacy chat/insight/insights map to `ask` on the server. */
export type ChatMode =
  | 'ask'
  | 'insight'
  | 'chat'
  | 'control'
  | 'agent'
  | 'master'
  | 'employee';

export interface WsSendPayload {
  token: string;
  message: string;
  mode: ChatMode;
  station_id?: string;
  conversation_id?: string;
  /** Required for mode=employee: device agent id, e.g. device_rayong_sp-1 */
  agent_id?: string;
  /** Optional: for employee mode intent (insight | control; legacy ask) */
  intent?: string;
  /** Optional — server merges from localStorage for control if omitted */
  username?: string;
  password?: string;
  org_id?: string;
  org_name?: string;
}

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30_000;

function wsMessageDedupeId(msg: {
  agent_id?: string;
  message_type?: string;
  content?: string;
  timestamp?: string | number;
}): string {
  const c = msg.content || '';
  let h = 5381;
  for (let i = 0; i < c.length; i++) {
    h = (h << 5) + h + c.charCodeAt(i);
  }
  const ts = msg.timestamp == null ? '' : String(msg.timestamp);
  return `${msg.agent_id || ''}|${msg.message_type || ''}|${ts}|${h >>> 0}|${c.length}|${c.slice(0, 96)}`;
}

export function useChatWS() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  const cancelledRef = useRef(false);
  const clientStoppedRef = useRef(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [status, setStatus] = useState<WsStatus>('connecting');
  const [fullState, setFullState] = useState<FullTaskState | null>(null);
  const [taskId, setTaskId] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const seenRef = useRef(new Set<string>());

  // ── Message handler ───────────────────────────────────────────────────────
  const handleFrame = useCallback((event: MessageEvent) => {
    if (clientStoppedRef.current) return;
    let frame: Record<string, unknown>;
    try {
      frame = JSON.parse(event.data as string);
    } catch {
      return;
    }

    const type = frame.type as string;

    if (type === 'ping') return;

    if (type === 'task_started') {
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
      seenRef.current.clear();
      setMessages([]);
      setFullState(null);
      setTaskId(frame.task_id as string);
      setConversationId(frame.conversation_id as string);
      setStatus('streaming');
      return;
    }

    if (type === 'partial') {
      const delta = String(frame.content_delta || '');
      const partialAgentId = String(frame.agent_id || '');
      const partialMessageType = String(frame.message_type || '');
      const partialAgentName = String(frame.agent_name || '');
      setMessages((prev) => {
        const next = [...prev];
        const i = next.findIndex(
          (m) =>
            m.agent_id === partialAgentId &&
            m.message_type === partialMessageType &&
            (m as ChatMessage & { __streaming?: boolean }).__streaming,
        );
        if (i >= 0) {
          next[i] = { ...next[i], content: next[i].content + delta };
        } else {
          next.push({
            agent_id: partialAgentId,
            agent_name: partialAgentName,
            message_type: partialMessageType,
            content: delta,
            timestamp: Date.now(),
            __streaming: true,
          } as ChatMessage & { __streaming?: boolean });
        }
        return next;
      });
      return;
    }

    if (type === 'update' || type === 'completed') {
      const state = frame as unknown as FullTaskState;
      setFullState(state);
      if (state.conversation_id) {
        setConversationId(state.conversation_id);
      }
      const incoming = state.messages || [];
      const finalizeKeys = new Set(
        incoming.map((msg) => `${msg.agent_id || ''}|${msg.message_type || ''}`),
      );
      setMessages((prev) => {
        let next = prev.filter((m) => {
          if (!(m as ChatMessage & { __streaming?: boolean }).__streaming) return true;
          // Terminal frame: every partial row is either superseded by the final
          // payload or an orphan (its head was finalized away mid-stream) that
          // would leak truncated text into the transcript — drop them all.
          // Thai: เฟรมจบงานต้องทิ้งแถว streaming ที่ค้าง กัน JSON หัวขาดหลุดขึ้นจอ
          if (type === 'completed') return false;
          return !finalizeKeys.has(`${m.agent_id || ''}|${m.message_type || ''}`);
        });
        incoming.forEach((msg) => {
          const id = wsMessageDedupeId(msg);
          if (!seenRef.current.has(id)) {
            seenRef.current.add(id);
            next = [...next, msg];
          }
        });
        return next;
      });
      if (type === 'completed') {
        const terminal = state.task_status === 'stopped' ? 'stopped' : 'completed';
        setStatus(terminal);
        const delayMs = 2500;
        completionTimerRef.current = setTimeout(() => setStatus('idle'), delayMs);
      }
      return;
    }

    if (type === 'error') {
      setError((frame.message as string) || 'Unknown error');
      setStatus(frame.code === 'timeout' ? 'timeout' : 'error');
      return;
    }
  }, []);

  // ── Connect / reconnect ────────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (cancelledRef.current) return;
    setStatus('connecting');

    const baseUrl = getWsChatUrl();
    const token = localStorage.getItem('admin_token') || '';
    const url = token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      setStatus('idle');
      setError(null);
    };

    ws.onmessage = handleFrame;

    ws.onerror = () => {
      if (cancelledRef.current) return;
      setStatus('error');
    };

    ws.onclose = () => {
      if (cancelledRef.current) return;
      if (clientStoppedRef.current) {
        clientStoppedRef.current = false;
        attemptRef.current = 0;
        reconnectTimerRef.current = setTimeout(connect, 200);
        return;
      }
      // Reconnect with exponential backoff (same pattern as usewarnWS.ts)
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attemptRef.current, RECONNECT_MAX_MS);
      attemptRef.current += 1;
      reconnectTimerRef.current = setTimeout(connect, delay);
    };
  }, [handleFrame]);

  // ── Mount: open WS, cleanup on unmount ────────────────────────────────────
  useEffect(() => {
    cancelledRef.current = false;
    connect();

    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // ── Send a message (starts a new task turn) ───────────────────────────────
  const send = useCallback((payload: WsSendPayload) => {
    if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      setError('WebSocket is not connected');
      setStatus('error');
      return;
    }
    seenRef.current.clear();
    setMessages([]);
    setFullState(null);
    setTaskId('');
    setConversationId('');
    setError(null);
    setStatus('streaming');
    const needsControlCreds =
      payload.mode === 'control' ||
      (payload.mode === 'employee' && payload.intent === 'control');
    const org =
      payload.org_id?.trim() ||
      payload.org_name?.trim() ||
      payload.station_id?.trim() ||
      getStoredLoginOrgName();
    let wire: Record<string, unknown> = {
      ...payload,
      ...(org ? { org_id: org, org_name: org } : {}),
    };
    if (needsControlCreds) {
      const u = payload.username?.trim() || getStoredLoginUsername();
      const pw = payload.password?.trim() || getStoredDeviceCommandPassword();
      wire = {
        ...wire,
        ...(u ? { username: u } : {}),
        ...(pw
          ? { password: pw, device_password: pw }
          : {}),
      };
    }
    wsRef.current?.send(JSON.stringify(wire));
  }, []);

  const cancelCurrentTask = useCallback(() => {
    clientStoppedRef.current = true;
    seenRef.current.clear();
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    setMessages([]);
    setError(null);
    setTaskId('');
    setStatus('stopped');
    completionTimerRef.current = setTimeout(() => setStatus('idle'), 150);
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      wsRef.current.close(1000, 'client_stop');
    }
  }, []);

  return {
    send,
    cancelCurrentTask,
    messages,
    status,
    fullState,
    taskId,
    conversationId,
    error,
  };
}
